import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Badge, Button, Card, Skeleton } from "./ui";
import { formatDate, formatDateTime } from "../lib/format";
import { ATTENDANCE_LABEL } from "../lib/people";
import type { AttendanceRegularization } from "../api/types";

/**
 * Attendance corrections: the employee asks, the rules say who decides.
 *
 * Filing writes nothing to the register; approval applies the change and leaves
 * an audit row (shown as a correction). The buttons are offered only when the
 * server says this caller may decide -- `can_decide` is the same rulebook the
 * approve endpoint enforces, so the screen can never offer a refused action.
 * The register values the request was filed against are shown beside the claim.
 *
 * Two surfaces share one row renderer: the employee's own panel, and the
 * review queue an owner or manager works through. The rules differ only in who
 * may act, and that difference is the server's `can_decide`, not the client's.
 */
export function AttendanceCorrections({
  employeeId,
  canManage,
  timezone,
}: {
  employeeId: string;
  canManage: boolean;
  timezone?: string | null;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const me = useQuery({
    queryKey: ["employee-me"],
    queryFn: ({ signal }) => api.employeeMe(signal),
    retry: false,
  });

  const requests = useQuery({
    queryKey: ["regularizations", employeeId],
    queryFn: ({ signal }) =>
      api.attendanceRegularizations({ employee: employeeId, page_size: 20 }, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["regularizations", employeeId] });
    void queryClient.invalidateQueries({ queryKey: ["employee-attendance", employeeId] });
  };

  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" | "cancel" }) => {
      if (action === "approve") return api.approveAttendanceRegularization(id);
      if (action === "reject") return api.rejectAttendanceRegularization(id);
      return api.cancelAttendanceRegularization(id);
    },
    onSuccess: invalidate,
  });

  const rows = requests.data?.results ?? [];

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Attendance corrections
        </h3>
        {!open ? (
          <Button onClick={() => setOpen(true)}>
            <Plus size={14} /> Request correction
          </Button>
        ) : null}
      </div>

      {open ? (
        <AttendanceCorrectionForm
          employeeId={employeeId}
          onDone={() => {
            setOpen(false);
            invalidate();
          }}
          onCancel={() => setOpen(false)}
        />
      ) : null}

      {requests.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">
          No correction requests. A missed check-out can be fixed here and routed to the
          reporting manager.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((request) => (
            <CorrectionRow
              key={request.id}
              request={request}
              timezone={timezone}
              busy={decide.isPending}
              canDecide={canManage}
              onApprove={() => decide.mutate({ id: request.id, action: "approve" })}
              onReject={() => decide.mutate({ id: request.id, action: "reject" })}
              onCancel={
                me.data?.id === request.employee
                  ? () => decide.mutate({ id: request.id, action: "cancel" })
                  : undefined
              }
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * The review queue: every correction request the caller may see, in one list.
 *
 * The owner sees the whole account; a manager sees their team. The server
 * hides the requests nobody may read -- another manager's request is the
 * owner's alone -- so a request missing from this list is not an oversight.
 * `can_decide` decides whether a row carries Approve and Reject.
 */
export function AttendanceCorrectionQueue({
  requests,
  timezone,
  onDecided,
}: {
  requests: AttendanceRegularization[];
  timezone?: string | null;
  onDecided?: () => void;
}) {
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      action === "approve"
        ? api.approveAttendanceRegularization(id)
        : api.rejectAttendanceRegularization(id),
    onSuccess: () => onDecided?.(),
  });

  if (requests.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No correction requests. Requests filed by employees appear here for review.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-slate-100">
      {requests.map((request) => (
        <CorrectionRow
          key={request.id}
          request={request}
          timezone={timezone}
          busy={decide.isPending}
          showRequester
          canDecide
          onApprove={() => decide.mutate({ id: request.id, action: "approve" })}
          onReject={() => decide.mutate({ id: request.id, action: "reject" })}
        />
      ))}
    </ul>
  );
}

/**
 * One correction request, and whatever the caller may do about it.
 *
 * The reviewer is named once the request has been decided, with the moment it
 * was decided and the note left behind -- a correction that changes somebody's
 * working day has to say who changed it and when.
 */
function CorrectionRow({
  request,
  timezone,
  busy,
  canDecide,
  showRequester = false,
  onApprove,
  onReject,
  onCancel,
}: {
  request: AttendanceRegularization;
  timezone?: string | null;
  busy: boolean;
  canDecide: boolean;
  showRequester?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
}) {
  const pending = request.status === "pending";
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5">
      <div className="min-w-0">
        <p className="text-sm text-slate-800">
          {showRequester && request.employee_name
            ? `${request.employee_name} · `
            : ""}
          {formatDate(request.work_date)}
          {request.requested_status_display
            ? ` · ${request.requested_status_display}`
            : ""}
          {request.requested_clock_in_at
            ? ` · in ${formatDateTime(request.requested_clock_in_at, timezone ?? undefined)}`
            : ""}
          {request.requested_clock_out_at
            ? ` · out ${formatDateTime(request.requested_clock_out_at, timezone ?? undefined)}`
            : ""}
        </p>
        <p className="text-xs text-slate-500">
          {request.reason || "No reason given"}
          {request.approver_name ? ` · approver: ${request.approver_name}` : ""}
          {pending && request.approval_available === false
            ? " · no approver: the account has no owner besides the requester"
            : ""}
        </p>
        {request.original_status || request.original_clock_in_at ? (
          <p className="text-xs text-slate-400">
            Register said:{" "}
            {request.original_status
              ? request.original_status.replace(/_/g, " ")
              : "no status"}
            {request.original_clock_in_at
              ? ` · in ${formatDateTime(request.original_clock_in_at, timezone ?? undefined)}`
              : ""}
            {request.original_clock_out_at
              ? ` · out ${formatDateTime(request.original_clock_out_at, timezone ?? undefined)}`
              : ""}
          </p>
        ) : null}
        {pending ? (
          <p className="text-xs text-slate-400">Awaiting review.</p>
        ) : (
          <p className="text-xs text-slate-400">
            {request.decided_by_name
              ? `Reviewed by ${request.decided_by_name}`
              : "Reviewed"}
            {request.decided_at
              ? ` · ${formatDateTime(request.decided_at, timezone ?? undefined)}`
              : ""}
            {request.decision_note ? ` · ${request.decision_note}` : ""}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Badge
          tone={
            request.status === "approved"
              ? "emerald"
              : request.status === "rejected"
                ? "rose"
                : "amber"
          }
        >
          {request.status_display}
        </Badge>
        {canDecide && pending && request.can_decide ? (
          <>
            <Button
              variant="ghost"
              disabled={busy}
              title="Approve"
              onClick={onApprove}
            >
              Approve
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              title="Reject"
              onClick={onReject}
            >
              Reject
            </Button>
          </>
        ) : null}
        {canDecide && pending && !request.can_decide ? (
          <span className="text-xs text-slate-400">
            {request.approver_name ? `With ${request.approver_name}` : "With the owner"}
          </span>
        ) : null}
        {pending && !canDecide && onCancel ? (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The form an employee uses to claim a correction.
 *
 * The corrected punches are validated by the server -- a check-out before the
 * check-in is refused there, not here -- so this only refuses an empty day.
 */
export function AttendanceCorrectionForm({
  employeeId,
  onDone,
  onCancel,
}: {
  employeeId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [workDate, setWorkDate] = useState("");
  const [status, setStatus] = useState("present");
  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");
  const [reason, setReason] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      api.createAttendanceRegularization({
        employee: employeeId,
        work_date: workDate,
        requested_status: status as "present" | "absent" | "half_day" | "on_leave",
        requested_clock_in_at: clockIn ? new Date(clockIn).toISOString() : null,
        requested_clock_out_at: clockOut ? new Date(clockOut).toISOString() : null,
        reason: reason.trim(),
      }),
    onSuccess: onDone,
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!workDate) return;
        submit.mutate();
      }}
      className="mb-3 grid gap-3 border-b border-slate-100 pb-3 sm:grid-cols-2"
    >
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-slate-600">Work date</span>
        <input
          type="date"
          required
          value={workDate}
          onChange={(event) => setWorkDate(event.target.value)}
          className={inputClass}
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-slate-600">Corrected status</span>
        <select value={status} onChange={(event) => setStatus(event.target.value)} className={inputClass}>
          <option value="present">Present</option>
          <option value="half_day">Half day</option>
          <option value="absent">Absent</option>
          <option value="on_leave">On leave</option>
        </select>
      </label>
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-slate-600">Check-in</span>
        <input
          type="datetime-local"
          value={clockIn}
          onChange={(event) => setClockIn(event.target.value)}
          className={inputClass}
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-slate-600">Check-out</span>
        <input
          type="datetime-local"
          value={clockOut}
          onChange={(event) => setClockOut(event.target.value)}
          className={inputClass}
        />
      </label>
      <label className="block text-sm sm:col-span-2">
        <span className="mb-1 block text-xs font-medium text-slate-600">Reason</span>
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="e.g. Forgot to check out"
          className={inputClass}
        />
      </label>
      {submit.error instanceof ApiError ? (
        <p role="alert" className="text-sm text-rose-600 sm:col-span-2">
          {submit.error.message}
        </p>
      ) : null}
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={submit.isPending || !workDate}>
          Submit request
        </Button>
      </div>
    </form>
  );
}

/**
 * The audit trail: the days that were corrected, and who corrected them.
 *
 * A correction is written to the register as a manual row, so the trail is the
 * register itself -- filtered to the rows a human changed.
 */
export function AttendanceCorrectionHistory({ employeeId }: { employeeId: string }) {
  const query = useQuery({
    queryKey: ["employee-attendance", employeeId],
    queryFn: ({ signal }) => api.attendancePage({ employee: employeeId, page_size: 30 }, signal),
  });
  const corrected = (query.data?.results ?? []).filter((row) => row.source === "manual");
  if (corrected.length === 0) return null;
  return (
    <Card>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Recent corrections
      </h3>
      <ul className="divide-y divide-slate-100">
        {corrected.slice(0, 5).map((row) => (
          <li key={row.id} className="flex items-center justify-between py-2 text-sm">
            <span className="text-slate-600">{formatDate(row.work_date)}</span>
            <span className="text-xs text-slate-500">
              {ATTENDANCE_LABEL[row.status ?? "present"]} · {row.marked_by_name || "—"}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";
